
import { useRuntimeConfig } from "#imports";
import { defineStore } from 'pinia';
import { ref, computed, watch } from "vue";
import { SupabaseClient } from "@supabase/supabase-js";
import type { User as SupabaseUser, Provider } from "@supabase/supabase-js";
import { useLogger } from "../composables/useLogger";
import { useSupabaseClientStore } from "./supaBaseClientStore";
import { Helper } from "../src/usermodel/Helper";
import { User } from "../src/usermodel/User";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import { Person } from "../src/usermodel/Person";
import { initializeStartWizardStatus } from "./startWizardStore";
import { UserCustomerStorage } from "../src/storage/UserCustomerStorage";
import type {
  UserHelperState,
  LatestAreaType,
} from "../src/storage/UserCustomerStorage";
import { useAuthRedirects } from "../composables/useAuthRedirects";
import { HELPER_ROLE_MAINHELPER, SKIP_NAME_INFO, ANNIE_ELK_USER_ID } from "../src/constants";
import { SkipNameInfo } from "../src/types";
import { ImageGame } from "../src/models/ImageGame";
import { AlbumImage } from "../src/models/AlbumImage";
import { useDeleteUserService } from "../composables/useDeleteUserService";
// import { useNuxtApp } from "#imports";
import { useWelcomeService } from "../composables/useWelcomeService";
import CryptoJS from 'crypto-js';
import QRCode from 'qrcode';
import { QrCode } from "../src/usermodel";

export interface AuthUser extends SupabaseUser {
  id: string;
  email?: string;
  app_metadata: {
    provider?: string;
    [key: string]: any;
  };
  user_metadata: {
    [key: string]: any;
  };
  aud: string;
  created_at: string;
}

export interface GoogleAuthResponse {
  rawCredential: string;
  email: string;
  firstName: string;
  lastName: string;
}

export const useAuthStore = defineStore("auth", () => {
  const logger = useLogger();
  const router = useRouter();
  const route = useRoute();
  const userStorage = UserCustomerStorage.getInstance();
  const {
    getCallbackPasswordResetUrl,
    getCallbackSignupUrl,
    getLandingInviteUrl,
  } = useAuthRedirects();
  const config = useRuntimeConfig();
  const encryptionKey = config.public.qrEncryptionKey;
  
  // QR code related state
  const qrCodeDataUrl = ref('');
  const isGeneratingQrCode = ref(false);
  const qrCodeError = ref<string | null>(null);

  // // Function to get i18n instance when needed
  // const getI18n = () => {
  //   const nuxtApp = useNuxtApp();
  //   return nuxtApp.$i18n as { t: (key: string) => string };
  // };

  // can be null, if not null indicates that we know the identity of a Person that is helper,
  // but we may no yet know _which_ helper it is.
  const authIdentity = ref<AuthUser | null>(null);

  // can be null, if not null indicates full login and contains the correct userId and helperId
  const uhstate = ref<UserHelperState | null>(null);

  // set dynamically when user is logged in - dynamically denotes where they last were.
  // If not there, signifies that we should check IndexDB for the latest area
  const latestArea = ref<LatestAreaType | null>(null);

  const userId = computed(() => {
    return uhstate.value?.userId;
  });

  const helperId = computed(() => {
    return uhstate.value?.helperId;
  });

  const user = ref<User | null>(null);
  const userPerson = ref<Person | null>(null);
  const helperForMultipleUsers = ref<boolean | null>(null);

  const helper = ref<Helper | null>(null);
  const helperPerson = ref<Person | null>(null);

  const isLockedToUserDevice = computed(() => {
    logger.debug("Auth: isLockedToUserDevice", {
      deviceLockStatus: uhstate.value?.deviceLockStatus,
    });
    if (uhstate.value?.deviceLockStatus === 'locked') {
      return true
    }
    return false
  });

  // slightly elaborate to make sure there are not "hanging watchers" when tests complete
  watch(
    helperId,
    async (newHelperId, oldHelperId) => {
      // Skip watcher if store is being disposed or if helperId is null
      if (!newHelperId) {
        helper.value = null;
        helperPerson.value = null;
        helperForMultipleUsers.value = null;
        return;
      }

      try {
        if (newHelperId !== oldHelperId) {
          const newHelper = await Helper.fromId(newHelperId);
          helper.value = newHelper;

          const newPerson = await newHelper.getPerson();
          helperPerson.value = newPerson;

          const usersForHelper = await newHelper.getAllUsersForHelperPerson();
          helperForMultipleUsers.value = usersForHelper.length > 1;
          logger.debug("Auth: helperForMultipleUsers", {
            helperForMultipleUsers: helperForMultipleUsers.value,
          });
        }
      } catch (error) {
        logger.logErrorCaught(
          "Auth: Error in helper watcher",
          "AuthStore.ts",
          error,
        );
        helper.value = null;
        helperPerson.value = null;
        helperForMultipleUsers.value = null;
      }
    },
    {
      immediate: false,
      flush: "sync", // Ensure watcher runs synchronously to avoid race conditions during disposal
    },
  );

  // Add watcher for userId to manage user and userPerson refs
  watch(
    userId,
    async (newUserId, oldUserId) => {
      // Skip watcher if store is being disposed or if userId is null
      if (!newUserId) {
        user.value = null;
        userPerson.value = null;
        return;
      }

      try {
        if (newUserId !== oldUserId) {
          const newUser = await User.fromId(newUserId);
          user.value = newUser;

          const newPerson = await newUser.getPerson();
          userPerson.value = newPerson;
        }
      } catch (error) {
        logger.logErrorCaught(
          "Auth: Error in user watcher",
          "AuthStore.ts",
          error,
        );
        user.value = null;
        userPerson.value = null;
      }
    },
    {
      immediate: false,
      flush: "sync", // Ensure watcher runs synchronously to avoid race conditions during disposal
    },
  );

  const userLoggedIn = computed(() => {
    // enough that uhstate is set, we know that we are a user and which one
    return uhstate.value != null;
  });

  const mustChooseHelperRole = computed(() => {
    return authIdentity.value != null && uhstate.value == null;
  });

  const helperLoggedIn = computed(() => {
    // both authIdentity and uhstate must be set, so that we know that the Helper is both well-known and chosen and authenticated
    return authIdentity.value != null && uhstate.value != null;
  });

  const idForPostHogLogging = computed(() => {
    if (userLoggedIn.value) {
      const userId = uhstate.value?.userId;
      return `${userId}_u`;
    } else {
      return "no_login";
    }
  });

  const isPEorME = computed(() => {
    if (authIdentity.value) {
      return (
        authIdentity.value.email?.endsWith("morten@elk.dk") ||
        authIdentity.value.email?.endsWith("mortenelk@gmail.com") ||
        authIdentity.value.email?.endsWith("peter@engelbrecht.dk")
      );
    }
    return false;
  });

  const isAdmin = computed(() => {
    return isPEorME.value;
  });

  const isInternalForAudio = computed(() => {
    return (
      isPEorME.value ||
      authIdentity.value?.email?.endsWith("carstenelk@gmail.com") ||
      authIdentity.value?.email?.endsWith("nils@engelbrecht.dk")
    );
  });

  const supabaseClientStore = useSupabaseClientStore();
  const supabase = computed(
    () => supabaseClientStore.supabase as SupabaseClient,
  );

  async function getLatestArea(): Promise<LatestAreaType> {
    logger.debug("Auth: getLatestArea", {
      current: latestArea.value,
      uhstate: uhstate.value,
    });
    if (latestArea.value) {
      return latestArea.value;
    }
    if (uhstate.value) {
      return "Helper";
    }
    return "User";
  }


  async function loginToUserDeviceFromQR(encryptedQrCodeId: string): Promise<void> {
    logger.debug("Auth: loginToUserDeviceFromQR", { encryptedQrCodeId });
    
    try {
      // Get the QR code from the encrypted string
      const qrCode = await QrCode.getQrCodeFromEncryptedString(encryptedQrCodeId);
      
      if (!qrCode) {
        logger.error("Auth: Invalid QR code", { encryptedQrCodeId });
        throw new Error("Invalid QR code");
      }
      
      // Check if QR code is valid
      if (!qrCode.isValid()) {
        logger.error("Auth: QR code is invalid or expired", { qrCodeId: qrCode.id });
        throw new Error("QR code is invalid or expired");
      }
      
      // Mark QR code as used
      await qrCode.markAsUsed();
      
      // Lock the device to the user associated with the QR code
      await lockToUserDevice(qrCode.user_id);
      
      logger.debug("Auth: Successfully locked device to user", { userId: qrCode.user_id });
    } catch (error) {
      logger.logErrorCaught("Auth: Error logging in from QR code", "AuthStore.ts", error);
      throw new Error("Failed to log in from QR code");
    }
  }

  /**
   * login is used to login a Helper user using email and password.
   * We find the authIdentity, register it in authStore and it is up to checkAuth and navigation to handle it from there
   * @param email
   * @param password
   */
  async function login(email: string, password: string): Promise<void> {
    logger.debug("Auth: Starting auth login process", { email });
    const { data, error } = await supabase.value.auth.signInWithPassword({
      email,
      password,
    });
    logger.debug("Auth: Supabase auth response received", {
      success: !!data?.user,
      userId: data?.user?.id,
    });

    if (error) {
      logger.error("Auth: Error in SignInWithPassword", { error });
      logger.throwSupabaseError(error, `Failed to sign in with email ${email}`);
    }
    // do a checkAuth to get a authStore state consistent with the login
    logger.debug("Auth: Starting checkAuth in login()");
    await checkAuth();
    logger.debug("Auth: Auth login process completed");
  }

  /**
   * loginWithGoogle is used to login an existing Helper user using Google auth.
   * We find the authIdentity, registrer it in authStore and it is up to checkAuth and navigation to handle it from there
   * @param googleAuthResponse
   */
  async function loginWithGoogle(googleAuthResponse: GoogleAuthResponse): Promise<void> {
    logger.debug("Auth: Starting Google login process", { googleAuthResponse });

    // test that there is a person with this email and google provider
    const person = await Person.getPersonAndProviderByEmail(googleAuthResponse.email)
    if (!person) {
      // logger.error("Auth: No person found with this email", { email: googleAuthResponse.email })
      throw new Error('No person found with this email')
    }
    if (person.provider !== 'google') {
      logger.error("Auth: Person found but not google is not the provider", { email: googleAuthResponse.email, provider: person.provider })
      throw new Error('Person found but not google is not the provider')
    }

    const { data, error } = await supabase.value.auth.signInWithIdToken({
      provider: 'google' as Provider,
      token: googleAuthResponse.rawCredential
    })
    logger.debug("Auth: Supabase auth response received", {
      success: !!data?.user,
      userId: data?.user?.id,
    });

    if (error) {
      logger.error("Auth: Error in SignInWithIdToken", { error });
      logger.throwSupabaseError(error, `Failed to sign in with Google`);
    }
    // do a checkAuth to get a authStore state consistent with the login
    logger.debug("Auth: Starting checkAuth in loginWithGoogle()");
    await checkAuth();
    logger.debug("Auth: Auth login process completed");
  }

  /*
    Does the full registration, including auth for a new user and the main helper for that user.
    Finally logs in the helper with checkAuth.
    This is the function to use for the normal registration process, using email and password.  
  */
  async function registerNewUserAndMainHelper(
    email: string,
    password: string,
    helperFirstName: string,
    helperLastName: string,
    userFirstName: string,
    userLastName: string,
  ) {
    logger.debug("Auth: Starting new user and helper registration", { email });

    // register the Helper in Supabase auth
    const newAuthUser = await registerNewAuthUser(email, password);
    logger.debug("Auth: Auth user registered", { authUserId: newAuthUser.id });

    // register the User and the Helper and all other starter data in our database
    await createNewUserDataWithMainHelper(
      newAuthUser,
      userFirstName,
      userLastName,
      helperFirstName,
      helperLastName,
      email,
    );
    logger.debug("Auth: User data registered");

    // ensure that we have an authStore state that reflects the new user
    logger.debug("Auth: Starting checkAuth in registerNewUserAndMainHelper()");
    await checkAuth();
    logger.debug("Auth: Registration complete", { uhstate: uhstate.value });
  }

  /**
   * Register a new user and a main helper with Google.
   * This is the function to use for the normal registration process, using Google.  
   * No Helper info is provided, as it is not needed for Google auth, which is already in place
   */
  async function registerNewUserAndMainHelperWithGoogle(
    googleAuthResponse: GoogleAuthResponse,
    email: string,
    helperFirstName: string,
    helperLastName: string,
    userFirstName: string,
    userLastName: string,
  ) {
    logger.debug("Auth: Starting new user and helper registration with Google/Supabase");
    try {
      const newAuthUser = await createGoogleAuthUser(googleAuthResponse, email)

      await createNewUserDataWithMainHelper(
        newAuthUser,
        userFirstName,
        userLastName,
        helperFirstName,
        helperLastName,
        email,
      );
      logger.debug("Auth: User data registered");
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Error registering new user and helper with Google/Supabase",
        "AuthStore.ts",
        error,
      );
    }
  }

  /**
   * Creates a new auth user using Google auth and returns it.
   * @param googleAuthResponse - The Google authentication response.
   * @param email - The email of the new auth user.
   * @returns The newly created auth user.
   */
  async function createGoogleAuthUser(
    googleAuthResponse: GoogleAuthResponse,
    email: string,
  ) {
    logger.debug('Auth: Checking if user exists (signup flow)', { email })
    const existingPerson = await Person.fromEmail(email)
    if (existingPerson) {
      logger.error('Auth: Person with this email already exists', { email })
      throw new Error('An account with this email already exists.')
    }

    // Only proceed with Supabase auth for new users
    const { data, error: errGoogleSignIn } = await supabase.value.auth.signInWithIdToken({
      provider: 'google' as Provider,
      token: googleAuthResponse.rawCredential
    })

    if (errGoogleSignIn) {
      logger.error('Auth: Error in Google sign-in', { errGoogleSignIn })
      logger.throwSupabaseError(errGoogleSignIn, 'Failed to sign in with Google during new auth user creation')
    }

    logger.debug('Auth: Google sign-in successful', {
      userId: data?.user?.id,
      email: data?.user?.email
    })

    const { data: { user: newAuthUser }, error } = await supabase.value.auth.getUser()
    if (error) logger.throwSupabaseError(error, "Failed to get auth user for Google/Supabase after creation");
    logger.debug("Auth: Google/Supabase auth user", { authUser: newAuthUser });

    return newAuthUser
}

  /*
    Registers a new auth user and person and returns the person.
    The auth user is registered with auth and a person is created.
  */
  async function registerPersonWithAuth(
    firstname: string,
    lastname: string,
    email: string,
    password: string,
  ) {
    const newAuthUser = await registerNewAuthUser(email, password);
    logger.debug("Auth: Auth user registered", { authUserId: newAuthUser.id });

    const person = await Person.createNewPerson(
      newAuthUser.id,
      firstname,
      lastname,
      email,
    );
    return person;
  }

  /*
    Creates the data for a new User and a new Helper and registers them in the database.
    The Helper toå be created already has an auth but that is the only pre-requisite.
    All other data related to a new user is created in this function (like start wizard status)
  */
  async function createNewUserDataWithMainHelper(
    newAuthUser: AuthUser,
    userFirstName: string,
    userLastName: string,
    helperFirstName: string,
    helperLastName: string,
    helperEmail: string,
  ) {
    logger.debug("Auth: Starting user data registration", {
      authUserId: newAuthUser.id,
      helperEmail,
    });

    const user = await createNewUserWithPerson(userFirstName, userLastName);
    const helper = await createNewHelperWithPerson(
      newAuthUser.id,
      helperFirstName,
      helperLastName,
      helperEmail,
      user,
    );

    logger.debug("Auth: User and helper registered in database", {
      userId: user.id,
      helperId: helper.id,
    });

    await initializeStartWizardStatus(user.id);
    logger.debug("Auth: Start wizard status initialized", { userId: user.id });

    try {
      const welcomeService = useWelcomeService()
      if (!(SKIP_NAME_INFO === SkipNameInfo.Skip)) {
        // send welcome email to helper ony works if we have a first name
        await welcomeService.sendWelcome(helperFirstName, helperEmail)
      }
    } catch (error) {
      logger.logErrorCaught("Auth: Error sending welcome", "AuthStore.ts", error)
    }
  }

  /*
    Creates a new User belonging to a new Person and registers it in the database.
    The User is created without an auth.
  */
  async function createNewUserWithPerson(
    firstnameUser: string,
    lastnameUser: string,
  ): Promise<User> {
    const personUser = await Person.createNewPerson(
      null,
      firstnameUser,
      lastnameUser,
      null,
    );
    const user = await User.createNewUser(personUser.id);

    return user;
  }

  /*
    Creates a new Helper belonging to a new Person and registers it in the database.
    The Helper is created without an auth.
  */
  async function createNewHelperWithPerson(
    authUserId: string,
    firstnameHelper: string,
    lastnameHelper: string,
    helperEmail: string,
    user: User,
  ): Promise<Helper> {
    const personHelper = await Person.createNewPerson(
      authUserId,
      firstnameHelper,
      lastnameHelper,
      helperEmail,
    );
    const helper = await Helper.createNewMainHelper(personHelper.id, user.id);

    return helper;
  }

  /*
    Registers a new auth user and person and creates a new assisting helper attaching that Person 
    to the user with the id userId.
    The auth user is registered with auth and a person is created.
    Then a new helper with the role "Assisting" is created for the person.
  */
  async function registerNewUserAndAssistingHelper(
    firstname: string,
    lastname: string,
    email: string,
    password: string,
    userId: string,
  ): Promise<Helper> {
    // check if the user exists
    const existingPerson = await Person.fromEmail(email)
    if (existingPerson) {
      logger.error('Auth: Person with this email already exists', { email })
      throw new Error('An account with this email already exists.')
    }

    // register auth and create the person
    const person = await registerPersonWithAuth(
      firstname,
      lastname,
      email,
      password,
    );
    logger.debug("Auth: Created new person", { personId: person.id });

    // Create new helper role for the person
    const helper = await Helper.createNewAssistingHelper(person.id, userId);
    logger.debug("Auth: Created new helper", { helperId: helper.id });

    return helper;
  }

  /**
   * Registers a new auth user and person and creates a new assisting helper attaching that Person 
   * to the user with the id userId.
   * The auth user is registered with Google auth in Supabase auth and a person is created.
   * @param {GoogleAuthResponse} googleCreds - The Google authentication credentials.
   * @param {string} firstname - The first name of the new person.
   * @param {string} lastname - The last name of the new person.
   * @param {string} userId - The ID of the user to assist.
   * @returns {Promise<Helper>} The newly created helper.
   */
  async function registerNewUserAndAssistingHelperWithGoogle(
    googleCreds: GoogleAuthResponse,
    firstname: string,
    lastname: string,
    userId: string,
  ): Promise<Helper> {
    logger.debug('Auth: Starting new assisting helper registration with Google', {
      email: googleCreds.email,
      userId: userId
    })

    try {
      const newAuthUser = await createGoogleAuthUser(googleCreds, googleCreds.email)

      // Create new person
      const person = await Person.createNewPerson(
        newAuthUser.id,
        firstname,
        lastname,
        googleCreds.email,
      )
      logger.debug("Auth: Created new person", { personId: person.id });

      // Create new helper role for the person
      const helper = await Helper.createNewAssistingHelper(person.id, userId);
      logger.debug("Auth: Created new helper", { helperId: helper.id });

      return helper
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Error registering new assisting helper with Google",
        "AuthStore.ts",
        error,
      );
      throw error
    }
  }

  /** 
   * Registers a new auth user in Supabase with email/password creds - to later be used for a Helper Person
   * @param {string} email - The email of the new auth user.
   * @param {string} password - The password of the new auth user.
   * @returns {Promise<AuthUser>} The newly created auth user.
   */
  async function registerNewAuthUser(
    email: string,
    password: string,
  ): Promise<AuthUser> {
    logger.debug("Auth: Starting auth user registration", { email });

    try {
      const authUser = await authSignUp(email, password);
      logger.debug("Auth: Auth user registration successful", {
        userId: authUser.id,
      });
      return authUser;
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Error registering auth user",
        "AuthStore.ts",
        error,
      );
      throw error;
    }
  }

  async function logout(): Promise<void> {
    logger.debug("Auth: Starting logout process");

    const { error } = await supabase.value.auth.signOut();
    if (error) {
      logger.logErrorCaught(
        "Auth: Error during Supabase signOut",
        "AuthStore.ts",
        error,
      );
      throw error;
    }
    logger.debug("Auth: Supabase signOut complete");

    // Clear browser session storage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes("sb-") && key.includes("auth-token")) {
        logger.debug("Auth: Removing session storage key", { key });
        sessionStorage.removeItem(key);
      }
    }

    logger.debug("Auth: Resetting auth state");
    authIdentity.value = null;
    uhstate.value = null;
    await userStorage.clearUserState();
    logger.debug("Auth: Logout complete");
  }

  /*
    Clears the uhstate, thus forcing an authed Helper to select a new user
  */
  async function switchUser(): Promise<void> {
    logger.debug("Auth: Switching user");
    // ensure that we have an auth user
    await checkAuth();
    if (!authIdentity.value) {
      logger.error("Auth: No auth user found, cannot switch user");
      throw new Error("No auth user found, cannot switch user");
    }
    // ensure that we have multiple users for the helper
    if (!helperForMultipleUsers.value) {
      logger.error(
        "Auth: No helper for multiple users found, cannot switch user",
      );
      throw new Error("No helper for multiple users found, cannot switch user");
    }
    // clear the uhstate in authStore
    uhstate.value = null;
    // clear the user state in userStorage
    await userStorage.clearUserState();
  }

  async function getSupabaseAuth(): Promise<void> {
    logger.debug("Auth: Getting Supabase auth session", {
      currentAuthUser: authIdentity.value?.id,
    });
    const { data } = await supabase.value.auth.getSession();

    const previousAuthUser = authIdentity.value?.id;
    authIdentity.value = data.session?.user as AuthUser | null;
    logger.debug("Auth: authuser set", { auhtUser: authIdentity.value });
    logger.debug("Auth: Supabase session retrieved", {
      hasSession: !!data.session,
      previousAuthUser,
      newAuthUser: authIdentity.value?.id,
      sessionChanged: previousAuthUser !== authIdentity.value?.id,
    });
  }

  async function setUhstateFromHelper(helperId: string) {
    logger.debug("Auth: Setting uhstate from helper", { helperId });
    const helper = await Helper.fromId(helperId);
    uhstate.value = {
      userId: helper.user_id!,
      helperId: helper.id,
      deviceLockStatus: uhstate.value?.deviceLockStatus || 'unknown'
    };
    logger.debug("Auth: Saving user state to storage:", {
      uhstate: uhstate.value,
    });
    const ucStorage = UserCustomerStorage.getInstance();
    await ucStorage.saveUserState(uhstate.value);
  }

  async function checkAuth(): Promise<void> {
    logger.debug("Auth: Starting auth check, in store:", {
      hasUhstate: !!uhstate.value,
      hasAuthUser: !!authIdentity.value,
    });

    if (uhstate.value) {
      logger.debug("Auth: checkAuth starting with uhstate:", {
        uhstate: uhstate.value,
      });
    } else {
      logger.debug("Auth: checkAuth starting without uhstate");
    }


    if (!uhstate.value) {
      logger.debug("Auth: No uhstate in authStore, checking storage");
      const storedState = await userStorage.getUserState();
      if (storedState) {
        logger.debug("Auth: Found stored state", { storedState: storedState });
        uhstate.value = storedState;
      } else {
        logger.debug("Auth: No stored state found");
      }
    }

    if (isLockedToUserDevice.value) {
      logger.debug("Auth: locked to user device");
      // ensure no auth user is set
      if (authIdentity.value) {
        authIdentity.value = null;
      }
      // that is all we need to do
      logger.debug("Auth: checkAuth complete, locked to user device");
      return;
    }

    const oldAuthUser = authIdentity.value;
    await getSupabaseAuth();

    if (authIdentity.value) {
      logger.debug("Auth: Auth user present after check", {
        authUserId: authIdentity.value.id,
        isNewUser: authIdentity.value.id !== oldAuthUser?.id,
      });

      if (authIdentity.value.id !== oldAuthUser?.id) {
        logger.debug("Auth: New auth user detected, trying to set uhstate");
        const authPerson = await Person.fromAuthId(authIdentity.value.id);
        if (!authPerson) {
          logger.error("Auth: Auth person not found for auth user", {
            authUserId: authIdentity.value.id,
          });
          throw new Error("Auth person not found for auth user");
        }
        const helpers = await authPerson.getAllHelperRolesPersonHas();
        if (helpers.length == 0) {
          logger.error("Auth: Auth person has no helpers", {
            authUserId: authIdentity.value.id,
          });
          throw new Error("Auth person has no helpers");
        }
        if (helpers.length == 1) {
          logger.debug(
            "Auth: Auth person has one helper, setting uhstate, from helper:",
            { helperId: helpers[0].id },
          );
          await setUhstateFromHelper(helpers[0].id);
        } else {
          // multiple helpers for Person, we need to signal that we need to select one
          // by only registering the authIdentity, but not the uhstate
          logger.debug(
            "Auth: Auth person has multiple helpers, do not register final uhstate",
          );
          uhstate.value = null;
        }
      }
    } else {
      logger.debug("Auth: No auth user after check");
    }
  }

  /**
   * cleanOutUserData is used to clean out all data associated with a user.
   * Auth is not cleared
   * Should only be used for testing or in conjunction with some full user deregistration process
   */
  async function cleanOutUserAndData(userId: string): Promise<void> {
    logger.debug("Auth: Starting user data cleanup", { userId });

    if (authIdentity.value) {
      logger.debug("Auth: Logging out before cleanup");
      await logout();
    }

    try {
      logger.debug("Auth: Fetching user data for cleanup");
      const userToDelete = await User.fromId(userId);
      const userPerson = await userToDelete.getPerson();
      const helpersToDelete = await userToDelete.getHelpers();
      const helperPersonsToDelete = await Promise.all(
        helpersToDelete.map((helper) => helper.getPerson()),
      );
      // first delete the relevant helpers
      for (const helperToDelete of helpersToDelete) {
        await Helper.delete(helperToDelete.id);
      }
      // then delete the helper persons, if no helpers are left now
      for (const personToDelete of helperPersonsToDelete) {
        const freshPerson = await Person.fromId(personToDelete.id);
        const freshHelpers = await freshPerson.getAllHelperRolesPersonHas();
        // delete the person if there are no helpers left
        if (freshHelpers.length == 0) {
          await Person.delete(freshPerson.id);
        }
      }
      // handle the user - and by DB cascade all the user content and setup
      await User.deleteUserSimple(userId);
      // and the userperson, if there are no helpers left on the person
      const helperRolesUserHas = await userPerson.getAllHelperRolesPersonHas();
      if (helperRolesUserHas.length == 0) {
        await Person.delete(userPerson.id);
      }

      logger.debug("Auth: User data cleanup complete", { userId });
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Error during user data cleanup",
        "AuthStore.ts",
        error,
      );
      throw error;
    }
  }

  async function getAuthUserFromSupabase(
    email: string,
    password: string,
  ): Promise<AuthUser> {
    logger.debug("Auth: Getting auth user from Supabase", { email });

    const { data, error } = await supabase.value.auth.signInWithPassword({
      email: email,
      password: password,
    });

    if (error) {
      logger.throwSupabaseError(error, "Failed to authenticate user");
    }

    logger.debug("Auth: Successfully retrieved auth user", {
      userId: data.user?.id,
    });
    return data.user as AuthUser;
  }

  async function authUserExistsInSupabase(
    email: string,
    password: string,
  ): Promise<boolean> {
    logger.debug("Auth: Checking if auth user exists", { email });

    const { data: loginData, error: loginError } =
      await supabase.value.auth.signInWithPassword({
        email: email,
        password: password,
      });

    if (loginError) {
      logger.debug("Auth: User does not exist or invalid credentials", {
        email,
      });
      return false;
    }

    logger.debug("Auth: User exists check result", {
      exists: !!loginData.user,
      userId: loginData.user?.id,
    });
    return loginData.user !== null;
  }

  /* =============================================================================
  Latest area
  ============================================================================= */
  async function setLatestArea(area: LatestAreaType): Promise<void> {
    logger.debug("Auth: Setting latest area", {
      newArea: area,
      currentArea: latestArea.value,
    });

    if (area !== latestArea.value) {
      const customerStorage = UserCustomerStorage.getInstance();
      await customerStorage.saveLatestArea(area);
      logger.debug("Auth: Saved new area to storage");
    }
    latestArea.value = area;
  }

  /*
    If latestArea is not set, we check IndexDB for the latest area, if User, set it in authStore and return true.
  */
  async function shouldRedirectToUserDueToLatestArea(): Promise<boolean> {
    logger.debug("Auth: Checking if should redirect to user", {
      currentLatestArea: latestArea.value,
    });

    if (latestArea.value) {
      logger.debug("Auth: Latest area already set, no redirect needed");
      return false;
    }

    const customerStorage = UserCustomerStorage.getInstance();
    const storedArea = await customerStorage.getLatestArea();
    logger.debug("Auth: Retrieved stored area", { storedArea });

    if (storedArea && storedArea === "User") {
      logger.debug("Auth: Setting latest area from storage", { storedArea });
      latestArea.value = storedArea;
      return true;
    }

    logger.debug("Auth: No redirect needed");
    return false;
  }

  /* =============================================================================
  For testing purposes
  ============================================================================= */
  async function ensureTestUserExistsAndIsLoggedIn(
    username: string,
    password: string,
    suffix: string = "",
  ) {
    const userEx = await authUserExistsInSupabase(username, password);

    if (!userEx) {
      await registerNewUserAndMainHelper(
        username,
        password,
        "T",
        "TstH" + suffix,
        "T",
        "TstU" + suffix,
      );
      logger.debug("Created new test user and customer entry");
    } else {
      logger.debug("Test user already exists");
    }

    logger.debug(
      "Auth: Starting checkAuth in ensureTestUserExistsAndIsLoggedIn()",
    );
    await checkAuth();
  }

  /*
    Ensures that a test user exists and is clean and logged in.
    If the user exists, it will be cleaned out and then a new user will be created and logged in for that email
  */
  async function ensureTestUserExistsAndIsCleanAndLoggedIn(
    email: string,
    password: string,
    suffix: string = "",
  ): Promise<void> {
    const authUserExistsAlready = await authUserExistsInSupabase(
      email,
      password,
    );
    if (authUserExistsAlready) {
      const authUserToUse = await getAuthUserFromSupabase(email, password);
      const helperPersonExists = await Person.existsByAuthId(authUserToUse.id);
      if (helperPersonExists) {
        const helpersToDelete = await Helper.getAllHelpersFromAuthUser(
          authUserToUse.id,
        );
        for (const helperToDelete of helpersToDelete) {
          const userIdToDelete = helperToDelete.user_id;
          await cleanOutUserAndData(userIdToDelete!);
        }
      }
      await createNewUserDataWithMainHelper(
        authUserToUse,
        "T",
        "TstU" + suffix,
        "T",
        "TstH" + suffix,
        email,
      );
      await login(email, password);
      logger.debug(
        "Auth: Starting checkAuth in ensureTestUserExistsAndIsCleanAndLoggedIn()",
      );
      await checkAuth();
    } else {
      await ensureTestUserExistsAndIsLoggedIn(email, password);
    }
  }

  async function selectHelperId(helperIdSelected: string): Promise<void> {
    logger.debug("Auth: selectHelper", { helperIdSelected: helperIdSelected });
    try {
      if (!mustChooseHelperRole.value) {
        logger.debug(
          "Auth: selectHelper, but mustChooseHelperRole is false, problem...",
        );
        throw new Error(
          "MustChooseHelperRole is false in selectHelperId, problem...",
        );
      }

      const helperSelected = await Helper.fromId(helperIdSelected);
      logger.debug("Auth: selected helper", { helperSelected: helperSelected });
      await setUhstateFromHelper(helperSelected.id);

      // check auth again to make sure we have the correct state
      logger.debug("Auth: Starting checkAuth in selectHelperId()");
      await checkAuth();

      logger.debug("Auth: Helper selected, values in store:", {
        userId: userId.value,
        helperId: helperId.value,
      });
    } catch (error) {
      throw error;
    }
  }

  // Add new methods for password reset
  const resetPasswordForEmail = async (email: string) => {
    const redirectTo = getCallbackPasswordResetUrl();

    logger.debug("Auth: Using redirect URL for password reset", {
      redirectTo: redirectTo,
    });

    try {
      const { data, error } = await supabase.value.auth.resetPasswordForEmail(
        email,
        {
          redirectTo,
        },
      );

      if (error) {
        logger.error("Auth: Password reset request failed", {
          error,
          email,
          code: error.code,
          message: error.message,
        });
        logger.throwSupabaseError(error, `Failed to reset password for email ${email}`);
      }

      logger.debug("Auth: Password reset email sent successfully", {
        email,
        data,
      });
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Unexpected error during password reset",
        "AuthStore.ts",
        error,
        { email },
      );
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    logger.debug("Auth: Starting password update");
    try {
      const { data, error } = await supabase.value.auth.updateUser({
        password: password,
      });

      if (error) {
        logger.error("Auth: Password update failed", {
          error,
          code: error.code,
          message: error.message,
          status: error.status,
        });
        logger.throwSupabaseError(error, `Failed to update password`);
      }

      logger.debug("Auth: Password update successful", {
        userId: data?.user?.id,
        updatedAt: data?.user?.updated_at,
      });
    } catch (error) {
      logger.error("Auth: Unexpected error during password update", { error });
      throw error;
    }
  };


  const authSignUp = async (
    email: string,
    password: string,
  ): Promise<AuthUser> => {
    const redirectTo = getCallbackSignupUrl();

    logger.debug("Auth: Using redirect URL for signup", { redirectTo });

    const { data, error } = await supabase.value.auth.signUp({
      email,
      password,
      options: {
        // User can immediately sign in after registration
        data: {
          confirmed_at: new Date().toISOString(),
        },
      },
    });

    logger.debug("Auth: Signed up");
    logger.debug("Auth: Signup data", { data });

    if (error) {
      logger.error("Auth: Signup failed", { error, email });
      logger.throwSupabaseError(error, "Failed to sign up new user");
    }
    logger.debug("Auth: Signup successful", {
      userId: data?.user?.id,
      email: data?.user?.email,
    });
    return data.user as AuthUser;
  };

  const setSession = async (accessToken: string, refreshToken?: string) => {
    // start by dropping any pre-existing session
    try {
      await supabase.value.auth.signOut();
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Error signing out before setting session",
        "AuthStore.ts",
        error,
      );
    }

    logger.debug("Auth: Setting session with access token");
    try {
      const session = {
        access_token: accessToken,
        refresh_token: refreshToken || "",
      };
      logger.debug("Auth: Setting session, Session object", { session });

      const { data, error } = await supabase.value.auth.setSession(session);

      if (error) {
        logger.error("Auth: Error in setSession", { error });
        logger.throwSupabaseError(error, `Failed to set session`);
      }

      // Set the user state after successful session
      if (data?.user) {
        logger.debug("Auth: Session set successfully", {
          userId: data.user.id,
          email: data.user.email,
        });

        return { success: true, data };
      } else {
        logger.error("Auth: No user data returned from session set", { data });
        throw new Error("No user data returned from session set");
      }
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Unexpected error setting session",
        "AuthStore.ts",
        error,
      );
      throw error;
    }
  };

  /**
   *
   */
  const canDeleteAccount = async (): Promise<{
    retval: boolean;
    explanation: string;
  }> => {
    let canDelete = false;
    let explanation = "";
    try {
      // check that Helper is Main Helper
      const helperRoles = await helper.value?.getHelperRoles();
      if (!helperRoles || helperRoles.length !== 1) {
        explanation = `Helper (${helper.value?.id}, ${helperPerson.value?.firstname} ${helperPerson.value?.lastname}) has no helper entries or more than 1 roles in Supabase`;
        return { retval: false, explanation };
      }

      if (
        !helperRoles.some((role) => role.helperrole === HELPER_ROLE_MAINHELPER)
      ) {
        explanation = "Helper is not a main helper";
        return { retval: false, explanation };
      }

      // get all Helpers for this User
      const allHelpers = await Helper.getAllHelpersForUser(user.value?.id);

      // check that all Persons that are Helpers for this User have only one Helper Role and that is for this user
      for (const theHelper of allHelpers) {
        const theHelperPerson = await theHelper.getPerson();
        // get all Helpers that this person has
        const helpersThatThisPersonIs =
          await theHelperPerson.getAllHelperRolesPersonHas();
        if (helpersThatThisPersonIs.length !== 1) {
          explanation = `A HelperPerson (${theHelperPerson.id}, ${theHelperPerson.firstname} ${theHelperPerson.lastname}) for this User is Helper for more than one User`;
          return { retval: false, explanation };
        }
      }

      // check that all Persons that are Helpers for this User are not themselves Users
      for (const theHelper of allHelpers) {
        const theHelperPerson = await theHelper.getPerson();
        const isUser = await User.userExistsForPerson(theHelperPerson.id);
        if (isUser) {
          explanation = `A HelperPerson (${theHelperPerson.id}, ${theHelperPerson.firstname} ${theHelperPerson.lastname}) for this User is a User themselves`;
          return { retval: false, explanation };
        }
      }

      // check that the user has no helper roles
      const userRoles = await userPerson.value?.getAllHelperRolesPersonHas();
      if (userRoles && userRoles.length > 0) {
        explanation = `User (${userPerson.value?.id}) has helper roles`;
        return { retval: false, explanation };
      }

      canDelete = true;
    } catch (error) {
      explanation = "unforeseen error in running canDeleteAccount";
      logger.logErrorCaught(
        "Auth: Error in canDeleteAccount",
        "AuthStore.ts",
        error,
      );
      return { retval: false, explanation };
    }

    return { retval: canDelete, explanation };
  };

  /**
   * deleteAccount is used to delete an account.
   */
  const deleteAccount = async (): Promise<void> => {
    logger.debug("Auth: Starting account deletion process");
    const { retval, explanation } = await canDeleteAccount();
    if (!retval) {
      throw new Error(explanation);
    }

    // record what we have to delete
    const allHelpersToDelete = await Helper.getAllHelpersForUser(
      user.value?.id,
    );
    const allHelperPersonsToDelete = await Promise.all(
      allHelpersToDelete.map((helper) => helper.getPerson()),
    );
    const theUserPersonToDelete = await user.value?.getPerson();
    const theUserToDelete = user.value;

    logger.debug("Auth: All helpers to delete", { allHelpersToDelete });
    logger.debug("Auth: All helper persons to delete", {
      allHelperPersonsToDelete,
    });
    logger.debug("Auth: The user person to delete", { theUserPersonToDelete });

    // delete all relevant images and blobs etc
    await AlbumImage.deleteAllAlbumImagesForUser(theUserToDelete.id);
    await ImageGame.deleteAllImageGamesForUser(theUserToDelete.id);

    // delete the helper persons - will cascade to the helpers
    for (const personToDelete of allHelperPersonsToDelete) {
      await Person.delete(personToDelete.id);
    }
    logger.debug("Auth: All helper persons deleted");

    // handle the user - and by DB cascade all the user content and setup that is not already deleted
    await Person.delete(userPerson.value?.id);
    logger.debug("Auth: User person deleted");

    const personsToDelete = allHelperPersonsToDelete;

    // delete the auth entries for the relevant User Person and Helper Persons
    await deleteAuthEntriesForPersons(personsToDelete);

    // sign out and redirect to /deleted
    await logout();
    router.push("/deleted-account");
  };

  /**
   * deleteAuthEntriesForPersons is used to delete the auth entries in supabase for the relevant User Person and Helper Persons
   * @param personsToDelete
   */
  async function deleteAuthEntriesForPersons(
    personsToDelete: Person[],
  ): Promise<void> {
    logger.debug("Auth: Starting deletion of auth entries", {
      personCount: personsToDelete.length,
    });

    // Filter out persons that have auth IDs and get the auth IDs directly
    const authIds = personsToDelete
      .filter((person) => person?.opt_auth_id)
      .map((person) => person.opt_auth_id!);

    if (authIds.length === 0) {
      logger.debug("Auth: No persons with auth IDs to delete");
      return;
    }

    try {
      const config = useRuntimeConfig();
      logger.debug("Auth: Config for deletion", {
        hasSecret: !!config.public.nestorServerApiSecret,
        secretLength: config.public.nestorServerApiSecret?.length,
        authIds,
      });

      const { deleteUsers } = useDeleteUserService();
      const response = await deleteUsers(authIds);

      logger.debug("Auth: Successfully deleted auth entries", {
        success: response.success,
        authCount: authIds.length,
      });
    } catch (error) {
      logger.logErrorCaught(
        "Auth: Error deleting auth entries",
        "AuthStore.ts",
        error,
      );
      throw error;
    }
  }

  /**
   * For testing purposes, log an error.
   */
  const testErrorLogging = async (): Promise<void> => {
    logger.error("Test error logging", { details1: "Here are details 1", details2: "Here are details 2" });
  }

  /**
   * For testing purposes, throw an error from the supabase client with interesting stack deep down through the model to the supabase client.
   */
  const testErrorLoggingwithThrow = async (): Promise<void> => {
    // throw new Error("Doing a manual throw in authStore");
    await User.throwErrorFromSupabase();
  }

  const decodeGoogleCredential = (credential: string): GoogleAuthResponse => {
    try {
      const payload = JSON.parse(atob(credential.split('.')[1]))
      return {
        rawCredential: credential,
        email: payload.email,
        firstName: payload.given_name,
        lastName: payload.family_name
      }
    } catch (error) {
      logger.logErrorCaught('Auth: Error decoding Google credential', 'AuthStore.ts', error)
      throw error
    }
  }

  /**
   * Encrypt a QR code ID for secure embedding in QR code
   * This ensures that only the Nestor app can decode it
   */
  function encryptQrCodeId(qrCodeId: string): string {
    // Add a timestamp to prevent replay attacks
    const payload = {
      id: qrCodeId,
      timestamp: Date.now()
    };
    // Convert to JSON string and encrypt
    return CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      encryptionKey
    ).toString();
  }

  /**
   * Decrypt a QR code ID from an encrypted string
   */
  function decryptQrCodeId(encryptedId: string): string {
    const decryptedData = CryptoJS.AES.decrypt(
      encryptedId,
      encryptionKey
    ).toString(CryptoJS.enc.Utf8);
    
    const payload = JSON.parse(decryptedData);
    return payload.id;
  }

  /**
   * Generate QR code from user ID
   * @param userId The user ID to encode in the QR code
   * @returns Promise that resolves to a data URL for the QR code
   */
  async function generateQrCodeForUser(userId: string): Promise<string> {
    try {
      isGeneratingQrCode.value = true;
      qrCodeError.value = null;
      
      // Get or create a valid QR code
      const qrCode = await QrCode.getOrCreateValidQrCode(userId);
      
      // Generate QR code data URL
      const dataUrl = await qrCode.generateQrCodeDataUrl();
      
      qrCodeDataUrl.value = dataUrl;
      isGeneratingQrCode.value = false;
      return dataUrl;
    } catch (err) {
      logger.logErrorCaught('Failed to generate QR code', 'authStore.ts', err);
      qrCodeError.value = 'Could not generate QR code. Please try again later.';
      isGeneratingQrCode.value = false;
      throw err;
    }
  }

  /**
   * Generate QR code for the current user
   * @returns Promise that resolves to a data URL for the QR code
   */
  async function generateCurrentUserQrCode(): Promise<string> {
    if (!userId.value) {
      qrCodeError.value = 'User ID not available. Please log in again.';
      throw new Error('User ID not available');
    }
    
    return generateQrCodeForUser(userId.value);
  }

  /**
   * Locks the current user to this device and implicitly logs them in
   */
  async function lockToUserDevice(userId: string): Promise<void> {
    logger.debug("Auth: Locking user to device", { userId: userId });
    const uhstateToSave : UserHelperState = {
      userId: userId,
      helperId: null,
      deviceLockStatus: 'locked'
    }
    logger.debug("Auth: Saving locked device state to storage:", {
      deviceLockStatus: uhstateToSave.deviceLockStatus,
    });
    const ucStorage = UserCustomerStorage.getInstance();
    await ucStorage.saveUserState(uhstateToSave);
    await checkAuth();
  }

  /**
   * Unlocks the current user from this device
   */
  async function unlockFromUserDevice(): Promise<void> {
    logger.debug("Auth: Unlocking user from device");
    if (!uhstate.value) {
      logger.debug("Auth: Cannot unlock from device, no uhstate");
      return;
    }
    
    uhstate.value = {
      ...uhstate.value,
      deviceLockStatus: 'unlocked'
    };
    
    logger.debug("Auth: Saving unlocked device state to storage:", {
      deviceLockStatus: uhstate.value.deviceLockStatus,
    });
    const ucStorage = UserCustomerStorage.getInstance();
    await ucStorage.saveUserState(uhstate.value);
  }

  // Clean up when store is destroyed

  return {
    isPEorME,
    isAdmin,
    isInternalForAudio,
    mustChooseHelperRole,
    userLoggedIn: userLoggedIn,
    helperLoggedIn: helperLoggedIn,
    idForPostHogLogging,
    userId,
    user,
    userPerson,
    helperForMultipleUsers,
    helperId,
    helper,
    helperPerson,
    latestArea,
    authIdentity,
    login,
    loginWithGoogle,
    loginToUserDeviceFromQR,
    logout,
    switchUser,
    checkAuth,
    selectHelperId,
    registerNewUserAndAssistingHelper,
    registerNewUserAndMainHelper,
    registerNewUserAndMainHelperWithGoogle,
    ensureTestUserExistsAndIsLoggedIn,
    ensureTestUserExistsAndIsCleanAndLoggedIn,
    resetPasswordForEmail,
    updatePassword,
    setLatestArea,
    shouldRedirectToUserDueToLatestArea,
    setSession,
    deleteAccount,
    testErrorLogging,
    testErrorLoggingwithThrow,
    decodeGoogleCredential,
    registerNewUserAndAssistingHelperWithGoogle,
    encryptQrCodeId,
    decryptQrCodeId,
    generateQrCode: generateQrCodeForUser,
    generateCurrentUserQrCode,
    qrCodeDataUrl,
    isGeneratingQrCode,
    qrCodeError,
    isLockedToUserDevice,
    lockToUserDevice,
    unlockFromUserDevice,
  };
});
